/* ============================================================================
 * ib-launches.js  -  window.IBLaunches
 *
 * HC - Launches page: cards, banner slots, rail, region gating and modal host
 * 2026-09-22; rebuilt to the New In v3 design 2026-10-13 (contract rev e, 0.6);
 * banner-only card slots 2026-10-14 (contract rev f, 0.7); two-column lead
 * rows 2026-10-14 (contract rev g, 0.8)
 *
 * Browser-global ES5 IIFE for sections/ib-launches.liquid. No build step, no
 * optional chaining. Defer-safe: no top-level DOM work; boot() runs on
 * DOMContentLoaded (or on the next tick when the document is already parsed).
 *
 * Pipeline:  config -> fetch feed (once) -> IBLaunchFeed.validateFeed
 *            -> card-level REGION gating -> layout plan -> cards + rail
 *            -> (CTA) launch modal
 *
 * Layers, top to bottom:
 *   1. STRINGS / sizing / colour + layout helpers
 *   2. templates          - markup only; every value escaped here
 *   3. access + modal seam - cardVisible, toModalPromo, createModalHost
 *   4. Section            - boot, fetch, states, delegated events, the
 *                           sticky rail, reveal motion
 *
 * Safety rules (contract section 6.4 - 6.6):
 *   - Every text value is escaped with IBLaunchFeed.escapeHtml (attributes
 *     included); rich text only via IBLaunchFeed.blocksToHtml.
 *   - Cards never reference the promo engine. Only the modal path does, behind
 *     modalAvailable(); without it a CTA navigates to destination.url.
 *   - Card-level gating is REGION ONLY (IBAccessPolicy.evaluatePromotionAccess
 *     with pricing restrictions off). Account gating happens inside the modal
 *     through the reused promo engine, exactly as on product/collection pages.
 *   - This file never writes to the cart, never fetches shop data itself and
 *     never reads product tags; the reused engine does all of that.
 *
 * Revision e (2026-10-13, contract section 0.6):
 *   - Layout: v3's grid. layoutPlan() ports v3's last-row rules, so a card
 *     left alone in the last row at the current width band renders as a
 *     feature (the whole row, story first). Rev g replaced v3's top feature,
 *     split and overlay cards with the lead rows below.
 *   - Card colours: launch.colours {background, text, accent} reach the DOM
 *     only as --ibl-card-bg / --ibl-card-ink / --ibl-card-accent in a card's
 *     style attribute, after each value passes ^#[0-9a-f]{6}$ here and text on
 *     background reaches 4.5:1. The card accent is darkened (lightened on a
 *     dark card) until it reaches 4.5:1 on the background, as v3's numColor
 *     does. Anything else gets the house palette from ib-launches.css.
 *   - Rail: a sticky "Jump to a launch" strip under the theme's sticky header
 *     (--ibl-sticky-offset, measured from .header-section). It tracks the
 *     launch in view and scrolls to a launch on click.
 *   - No window or document listeners: IntersectionObserver, ResizeObserver
 *     and matchMedia drive the rail, the reveal and the width bands.
 *
 * Revision f (2026-10-14, contract section 0.7):
 *   - One image per card, in a fixed-shape slot: the 2:3 portraitBanner where
 *     the image sits beside the story, the 3:2 banner where it sits above it.
 *     SLOT_TURN holds, per card type, the width at which its slot turns from
 *     3:2-above to 2:3-beside (ib-launches.css uses the same numbers). A card
 *     that turns renders one <picture> whose <source media> names the
 *     portrait image, so the browser fetches only the image for the current
 *     width; the first card's slot image is the page's one eager image.
 *   - A shape whose banner is null shows the first image whole on a white
 *     tile over the flat card colour (data-ibl-landscape / data-ibl-portrait
 *     = "tile"). No gallery, no blur, no runtime image measuring on cards; the
 *     launch modal keeps the full image gallery.
 *   - Derived colours (D-e4) sample banner, else portraitBanner, else the
 *     first image: one image per launch, whatever the width.
 *
 * Revision g (2026-10-14, contract section 0.8):
 *   - The first four launches are lead cards, two per row from 701px (span 6
 *     of 12). A lead is the stack card a size up: from 1081px its 2:3 slot
 *     sits beside the whole story (brand + kind pill, title, tagline, the
 *     two-sentence summary, highlights) with a filled button at the foot of
 *     the story column; below 1081px the 3:2 slot sits above the story. The
 *     kind pill shows on every lead by default (kindLabels 'lead').
 *   - The rest are stack cards; the last-row rules run on them alone. With
 *     fewer than four launches the leads still pair up, and an odd lead left
 *     alone in its row (one or three launches) is a feature from 701px.
 *   - Card types: lead, stack and feature. The top feature, split and
 *     overlay cards are gone.
 *
 * Revision j (2026-10-16, contract section 0.11): load-in motion.
 *   - The section renders a shimmering skeleton (count, rail, two lead
 *     cards); render() replaces it, and a retry puts it back (showSkeleton).
 *   - Every card takes part in the reveal: the ones on screen rise at once,
 *     the rest as they scroll in (prepReveal). The CSS settles the photo and
 *     staggers the story.
 *   - watchImages marks a slot whose photo the browser is still fetching
 *     (img.complete === false); the slot shimmers until the photo loads or
 *     fails, then the photo fades in. Reduced motion skips both.
 * ==========================================================================*/

window.IBLaunches = (function () {
  'use strict';

  /* ----------------------------------------------------------------------
   * 1. UI microcopy (English only, no locale keys by design) and constants
   * -------------------------------------------------------------------- */
  var STRINGS = {
    loading: 'Loading new launches…',
    loaded: function (n) { return n === 1 ? '1 new launch' : n + ' new launches'; },
    empty: 'New launches are on the way. Check back soon.',
    error: 'New launches couldn’t be loaded. Check your connection, then try again.',
    retry: 'Try again',
    kind: { brand: 'New brand', product: 'New product', collection: 'New collection' },
    cta: { brand: 'Shop the brand', product: 'Shop now', collection: 'Shop the collection' },
    count: function (n) { return n === 1 ? '1 launch' : n + ' launches'; },
    brands: function (n) { return 'from ' + (n === 1 ? '1 brand' : n + ' brands') + ', fresh this season'; },
    railLabel: 'Jump to a launch',
    railPrev: 'Scroll launches left',
    railNext: 'Scroll launches right',
    showImage: function (i, n) { return 'Show image ' + i + ' of ' + n; },
    galleryLabel: 'Launch images',
    highlights: 'Highlights',
    details: 'Details',
    inCollection: 'In this collection',
    filter: 'Filter',
    loadingProducts: 'Loading products…',
    loadingBuy: 'Loading…',
    close: 'Close'
  };

  var ICONS = {
    close: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" focusable="false"><path d="M18 6 6 18M6 6l12 12"/></svg>',
    filter: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true" focusable="false"><path d="M2 4h12M4 8h8M6 12h4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>',
    railPrev: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M10 3.5 5.5 8l4.5 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    railNext: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M6 3.5 10.5 8 6 12.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  };

  // Width bands (ib-launches.css uses the same breakpoints).
  var BANDS = {
    phone: '(max-width: 700px)',
    tablet: '(max-width: 1080px)',
    wide: '(min-width: 1401px)'
  };

  // Slot shapes (rev f, contract 0.7; rev g, 0.8): the width from which each
  // card type's slot is the 2:3 portrait beside the story; below it the slot
  // is the 3:2 landscape above the story. The same numbers drive
  // [data-ibl-turn] and the card layouts in ib-launches.css.
  var SLOT_TURN = { lead: 1081, feature: 961, stack: 1081 };

  // Shopify CDN widths (contract 6.8) and `sizes` per slot shape and card
  // type, matching ib-launches.css: 36px page padding (14px on phones), 24px
  // gaps; above slots run the card's full width. Beside slots: a lead card
  // is half the row, (100vw - 96px) / 2, less 14px padding a side; its slot
  // is 44% of that below 672px of card width (the lede layout, viewports up
  // to 1439px: 22vw - 33px), then min(40%, 380px) (20vw - 30px, 380px from
  // 2052px). A feature's is --ibl-feature-slot.
  var IMAGE_WIDTHS = [360, 540, 720, 900, 1080, 1440];
  var PHONE_SIZE = '(max-width: 700px) calc(100vw - 28px), ';
  var SIZES = {
    landscape: {
      lead: PHONE_SIZE + 'calc(50vw - 48px)',
      feature: PHONE_SIZE + 'calc(100vw - 72px)',
      stack: PHONE_SIZE + 'calc(50vw - 48px)'
    },
    portrait: {
      lead: '(max-width: 1439px) calc(22vw - 33px), (min-width: 2052px) 380px, calc(20vw - 30px)',
      feature: '(min-width: 1858px) 520px, 28vw',
      stack: '(max-width: 1400px) calc(21vw - 34px), (min-width: 2400px) 300px, calc(14vw - 30px)'
    }
  };
  var MODAL_SIZES = '(min-width: 901px) 460px, 100vw';
  var CARD_SRC_WIDTH = 720;
  var MODAL_SRC_WIDTH = 1080;
  var THUMB_WIDTH = 160;
  // The colour sample (D-e4): one small CORS copy of each launch's sampled
  // image, read on a SAMPLE_GRID x SAMPLE_GRID canvas.
  var SAMPLE_WIDTH = 64;
  var SAMPLE_GRID = 32;
  // How long the first render waits for the first cards' samples, so cards on
  // screen at load appear in their own colours instead of switching.
  var TONE_WAIT_MS = 400;
  var TONE_FIRST_CARDS = 3;

  function feed() { return window.IBLaunchFeed; }
  function esc(value) { return feed().escapeHtml(value); }
  function closest(el, selector) {
    return (el && el.closest) ? el.closest(selector) : null;
  }
  function imageAlt(image, launch) {
    return image.alt || launch.title;
  }
  function hasTextSelection() {
    if (typeof window.getSelection !== 'function') return false;
    var sel = window.getSelection();
    return !!(sel && !sel.isCollapsed && String(sel).length);
  }
  function pad2(n) { return n < 10 ? '0' + n : String(n); }
  function now() {
    return (window.performance && typeof window.performance.now === 'function') ? window.performance.now() : new Date().getTime();
  }
  function setVar(el, name, value) {
    if (el && el.style && typeof el.style.setProperty === 'function') el.style.setProperty(name, value);
  }
  function reducedMotion() {
    return !!(typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Rev j: a slot shimmers while its photo is still loading. Only an image
  // the browser reports as incomplete is marked, and the mark goes on load or
  // error, so a photo is never left hidden.
  function watchImage(img) {
    if (!img || img.complete !== false || typeof img.addEventListener !== 'function') return;
    var slot = closest(img, '[data-ibl-slot]');
    if (!slot) return;
    slot.setAttribute('data-ibl-img', 'wait');
    var done = function () {
      img.removeEventListener('load', done);
      img.removeEventListener('error', done);
      slot.removeAttribute('data-ibl-img');
    };
    img.addEventListener('load', done);
    img.addEventListener('error', done);
  }

  // The first `count` sentences of a paragraph (v3's smaller cards show two).
  function firstSentences(text, count) {
    var re = /[.!?](?=\s)/g;
    var seen = 0;
    var m;
    while ((m = re.exec(text)) !== null) {
      seen += 1;
      if (seen === count) return text.slice(0, m.index + 1);
    }
    return text;
  }
  // The summary block a lead or stack card shows: the first block, and when
  // it is a paragraph, its first two sentences. A feature and the modal
  // always show the summary in full (contract 0.6, 0.8).
  function summaryExcerpt(launch) {
    var first = launch.summary[0];
    if (first.type !== 'paragraph') return [first];
    return [{ type: 'paragraph', text: firstSentences(first.text, 2) }];
  }

  /* ---- Card colours (contract 0.6) ---- */
  var HEX = /^#[0-9a-f]{6}$/;
  function rgbOf(hex) {
    var n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function luminance(rgb) {
    var w = [0.2126, 0.7152, 0.0722];
    var sum = 0;
    for (var i = 0; i < 3; i++) {
      var v = rgb[i] / 255;
      v = v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
      sum += v * w[i];
    }
    return sum;
  }
  function contrastRatio(a, b) {
    var x = luminance(a);
    var y = luminance(b);
    return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
  }
  function toHex(rgb) {
    var out = '#';
    for (var i = 0; i < 3; i++) out += (rgb[i] < 16 ? '0' : '') + rgb[i].toString(16);
    return out;
  }
  // v3 numColor: keep the accent's hue, step it toward black (white on a dark
  // card) until it reaches 4.6:1 on the background (a hair over 4.5:1).
  function accentOn(accent, background) {
    var a = rgbOf(accent);
    var b = rgbOf(background);
    var to = luminance(b) > 0.18 ? 0 : 255;
    for (var s = 0; s <= 40; s++) {
      var c = [];
      for (var i = 0; i < 3; i++) c.push(Math.round(a[i] + (to - a[i]) * s / 40));
      if (contrastRatio(c, b) >= 4.6) return toHex(c);
    }
    return to ? '#ffffff' : '#000000';
  }
  // The page's own re-check: three lowercase #rrggbb strings, text on
  // background at 4.5:1 or more. Anything else returns null (house palette).
  function cardColours(launch) {
    var c = launch && launch.colours;
    if (!c || typeof c !== 'object') return null;
    var bg = c.background;
    var ink = c.text;
    var accent = c.accent;
    if (typeof bg !== 'string' || typeof ink !== 'string' || typeof accent !== 'string') return null;
    if (!HEX.test(bg) || !HEX.test(ink) || !HEX.test(accent)) return null;
    if (contrastRatio(rgbOf(ink), rgbOf(bg)) < 4.5) return null;
    return { bg: bg, ink: ink, accent: accent, mark: accentOn(accent, bg) };
  }

  // The only colour values written to the DOM: three re-checked properties.
  function colourStyleValue(colours, accent) {
    return '--ibl-card-bg:' + colours.bg + ';--ibl-card-ink:' + colours.ink + ';--ibl-card-accent:' + accent;
  }
  // The image a launch's derived colours are read from (D-e4, rev f): one per
  // launch whatever the width - the banner, else the portrait banner, else
  // the first image.
  function sampledImage(launch) {
    return launch.banner || launch.portraitBanner || launch.images[0];
  }

  // What a card's slot shows (rev f): the 3:2 landscape image, and for a card
  // type whose slot turns portrait, the 2:3 image and the width it turns at.
  // A shape whose banner is null falls back to the first image, shown whole
  // on a white tile (tile: true). alt = the banner's alt || title, or for a
  // tile the first image's alt || title (contract 6.4).
  function slotArt(launch, type) {
    var first = launch.images[0];
    var turn = SLOT_TURN[type] || null;
    function shape(banner) {
      if (banner) return { url: banner.url, alt: banner.alt || launch.title, tile: false };
      return { url: first.url, alt: imageAlt(first, launch), tile: true };
    }
    return {
      turn: turn,
      landscape: shape(launch.banner),
      portrait: turn ? shape(launch.portraitBanner) : null
    };
  }
  // The shape a slot shows now: portrait from its turn width up. Without
  // matchMedia the page renders the wide band, where every turn has passed.
  function portraitNow(turn) {
    if (!turn) return false;
    if (typeof window.matchMedia !== 'function') return true;
    return window.matchMedia('(min-width: ' + turn + 'px)').matches;
  }

  /* ---- Colours from a photo (rev e, D-e4) ----
   * paletteFromPixels(rgba) is pure: an RGBA byte array in, {background, text,
   * accent} (lowercase #rrggbb) or null out. Same pixels, same palette.
   *   - Transparent, near-white and near-black pixels never vote for a hue, so
   *     a product cut-out on white gives the product's colour, not white.
   *   - Mostly dark imagery (near-black content, or a dark photo) gives a deep
   *     tone with white text (v3: #0b0a0c, #1c1c1e).
   *   - Otherwise the dominant hue becomes a soft tint (v3: #f7cdd4, #cfe5d8;
   *     yellows a little deeper, #ffe066), with a near-black ink tinted toward
   *     it (aim 7:1, never under 4.5:1).
   *   - Too little colour (grey or white imagery) gives a quiet neutral.
   *   - Accent: the most saturated hue at least 45 degrees from the background
   *     hue; otherwise a darker (on dark cards, lighter) shade of the
   *     background hue. cardColours() then adjusts it to 4.5:1 as usual. */
  var HUE_BINS = 24;
  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
  function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var max = Math.max(r, g, b);
    var min = Math.min(r, g, b);
    var l = (max + min) / 2;
    var h = 0;
    var s = 0;
    if (max !== min) {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h *= 60;
    }
    return [h, s, l];
  }
  function hslToHex(h, s, l) {
    h = ((h % 360) + 360) % 360;
    var c = (1 - Math.abs(2 * l - 1)) * s;
    var x = c * (1 - Math.abs((h / 60) % 2 - 1));
    var m = l - c / 2;
    var rgb = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
    return toHex([Math.round((rgb[0] + m) * 255), Math.round((rgb[1] + m) * 255), Math.round((rgb[2] + m) * 255)]);
  }
  function hueDistance(a, b) {
    var d = Math.abs(a - b) % 360;
    return d > 180 ? 360 - d : d;
  }
  // Ink for a background: a near-black tinted toward its hue (or white on a
  // dark card), stepped until it reaches 7:1, never left under 4.5:1.
  function inkFor(bgHex, h, tinted) {
    var bg = rgbOf(bgHex);
    var white = contrastRatio([255, 255, 255], bg);
    var black = contrastRatio([0, 0, 0], bg);
    if (white > black) return white >= 4.5 ? '#ffffff' : '#000000';
    for (var l = 0.16; l >= 0.035; l -= 0.02) {
      var ink = hslToHex(h, tinted ? 0.5 : 0.08, l);
      if (contrastRatio(rgbOf(ink), bg) >= 7) return ink;
    }
    var deepest = hslToHex(h, tinted ? 0.5 : 0.08, 0.03);
    return contrastRatio(rgbOf(deepest), bg) >= 4.5 ? deepest : '#000000';
  }
  function paletteFromPixels(data) {
    if (!data || !data.length) return null;
    var bins = [];
    for (var b = 0; b < HUE_BINS; b++) bins.push({ w: 0, r: 0, g: 0, b: 0, s: 0 });
    var opaque = 0;
    var nK = 0;
    var nC = 0;
    var sumL = 0;
    var grey = { n: 0, r: 0, g: 0, b: 0 };
    var black = { r: 0, g: 0, b: 0 };
    for (var o = 0; o + 3 < data.length; o += 4) {
      if (data[o + 3] < 128) continue;
      opaque += 1;
      var r = data[o];
      var g = data[o + 1];
      var bl = data[o + 2];
      var max = Math.max(r, g, bl);
      var min = Math.min(r, g, bl);
      var chroma = max - min;
      if (min >= 226 && chroma <= 24) continue;                 // near-white: backdrop
      if (max <= 34) { nK += 1; black.r += r; black.g += g; black.b += bl; continue; }  // near-black
      nC += 1;
      var hsl = rgbToHsl(r, g, bl);
      sumL += hsl[2];
      if (chroma < 20) { grey.n += 1; grey.r += r; grey.g += g; grey.b += bl; continue; }  // grey: no hue vote
      // Vivid colour outvotes muted colour (skin, wood, amber glass): chroma squared.
      var weight = (chroma / 255) * (chroma / 255);
      var bin = bins[Math.floor(hsl[0] / (360 / HUE_BINS)) % HUE_BINS];
      bin.w += weight;
      bin.r += r * weight; bin.g += g * weight; bin.b += bl * weight;
      bin.s += hsl[1] * weight;
    }
    if (!opaque) return null;
    var content = nK + nC;
    var total = 0;
    for (b = 0; b < HUE_BINS; b++) total += bins[b].w;
    // Dominant hue: the heaviest bin with half its neighbours (hue is circular).
    var best = -1;
    var bestScore = 0;
    for (b = 0; b < HUE_BINS; b++) {
      var score = bins[b].w + 0.5 * (bins[(b + HUE_BINS - 1) % HUE_BINS].w + bins[(b + 1) % HUE_BINS].w);
      if (score > bestScore) { bestScore = score; best = b; }
    }
    var chromatic = best > -1 && total >= Math.max(0.5, content * 0.015);
    var dom = null;
    if (chromatic) {
      var acc = { w: 0, r: 0, g: 0, b: 0 };
      for (var k = -1; k <= 1; k++) {
        var nb = bins[(best + k + HUE_BINS) % HUE_BINS];
        acc.w += nb.w; acc.r += nb.r; acc.g += nb.g; acc.b += nb.b;
      }
      dom = rgbToHsl(acc.r / acc.w, acc.g / acc.w, acc.b / acc.w);
    }
    var meanL = content ? (sumL + nK * 0.05) / content : 1;
    var dark = content >= opaque * 0.05 && (nK / content >= 0.6 || meanL < 0.2);

    var h;
    var bg;
    var bgS;
    if (dark) {
      if (dom) { h = dom[0]; bgS = nK / content >= 0.6 ? 0.14 : clamp(dom[1], 0.25, 0.55); }
      else if (nK) { h = rgbToHsl(black.r / nK, black.g / nK, black.b / nK)[0]; bgS = 0.05; }
      else { h = 0; bgS = 0; }
      bg = hslToHex(h, bgS, nK / content >= 0.6 ? 0.07 : 0.14);
    } else if (dom) {
      h = dom[0];
      bgS = clamp(0.35 + 0.7 * dom[1], 0.3, 1);
      var bgL = 0.86;
      if (h >= 42 && h <= 68) { bgL = 0.76; bgS = Math.max(bgS, 0.8); }   // yellows read better deeper
      else if (h > 68 && h < 200) bgS = Math.min(bgS, 0.55);              // greens and cyans turn neon above this
      else if (h >= 200 && h < 250) bgS = Math.min(bgS, 0.75);
      bg = hslToHex(h, bgS, bgL);
    } else {
      h = grey.n ? rgbToHsl(grey.r / grey.n, grey.g / grey.n, grey.b / grey.n)[0] : 40;
      bgS = grey.n ? 0.05 : 0.08;
      bg = hslToHex(h, bgS, 0.9);
    }
    var text = inkFor(bg, h, !dark && bgS >= 0.15);

    // Accent: the most saturated hue clearly apart from the background hue.
    // On light cards a yellow accent only reaches 4.5:1 as olive, so any other
    // candidate goes first.
    var accent = null;
    var accentYellow = true;
    var bestSat = -1;
    var floor = Math.max(0.15, total * 0.02);
    for (b = 0; b < HUE_BINS; b++) {
      var bn = bins[b];
      if (bn.w < floor) continue;
      var bh = rgbToHsl(bn.r / bn.w, bn.g / bn.w, bn.b / bn.w);
      // On a near-black card any hue stands apart; elsewhere it must differ by 45 degrees.
      if (dom && hueDistance(bh[0], h) < 45 && (!dark || bgS >= 0.2)) continue;
      var yellow = !dark && bh[0] >= 40 && bh[0] <= 75;
      var sat = bn.s / bn.w;
      if ((accentYellow && !yellow) || (yellow === accentYellow && sat > bestSat + 1e-9)) {
        bestSat = sat;
        accent = bh;
        accentYellow = yellow;
      }
    }
    var accentHex;
    if (accent) {
      accentHex = dark
        ? hslToHex(accent[0], Math.max(accent[1], 0.55), clamp(accent[2], 0.6, 0.78))
        : hslToHex(accent[0], Math.max(accent[1], 0.55), clamp(accent[2], 0.32, 0.5));
    } else {
      accentHex = dark
        ? hslToHex(h, bgS > 0.1 ? 0.6 : 0, 0.75)
        : hslToHex(h, bgS > 0.1 ? clamp(bgS + 0.25, 0.45, 0.85) : 0.35, 0.32);
    }
    return { background: bg, text: text, accent: accentHex };
  }

  /* ---- Layout plan (rev g; v3's last-row rules, ported) ----
   * Launches 0-3 are lead cards ('lead'): two per row from 701px (span 6).
   * The rest are small cards: 3 per row from 1401px (span 4), 2 per row from
   * 701px (span 6). The small cards' last row fills itself: at >= 1401px a
   * short last row widens to span 6 (l3), and a card that would sit alone in
   * the last row of its band (l3 / t2 / l2 = 12) takes the whole row as a
   * feature. With fewer than four launches, an odd lead left alone in its
   * row (one or three launches) is a feature in every band from 701px.
   * Phones are one column: never a feature. */
  var LEAD_COUNT = 4;
  function layoutPlan(count) {
    var plan = [];
    var leads = Math.min(count, LEAD_COUNT);
    var rest = count - leads;
    var r3 = rest % 3;
    for (var i = 0; i < count; i++) {
      if (i < leads) {
        var alone = (leads % 2 === 1 && i === leads - 1) ? 12 : 0;
        plan.push({ pos: 'lead', l3: alone, t2: alone, l2: alone });
        continue;
      }
      var j = i - leads;
      var l3 = 0;
      if (r3 === 2 && j >= rest - 2) l3 = 6;
      else if (r3 === 1 && rest === 1) l3 = 12;
      else if (r3 === 1 && j >= rest - 4) l3 = 6;
      var last2 = (rest % 2 === 1 && j === rest - 1) ? 12 : 0;
      plan.push({ pos: 'small', l3: l3, t2: last2, l2: last2 });
    }
    return plan;
  }

  function cardType(slot, band) {
    if (band !== 'phone') {
      var span = band === 'tablet' ? slot.l2 : (band === 'wide' ? slot.l3 : slot.t2);
      if (span === 12) return 'feature';
    }
    return slot.pos === 'lead' ? 'lead' : 'stack';
  }

  // Rail labels are narrow: offer a line break after "." and "+" inside names
  // such as KEVIN.MURPHY+CODE (escaped first; <wbr> is the only markup added).
  function railLabelHtml(title) {
    return esc(title).replace(/([.+])(?=[A-Za-z])/g, '$1<wbr>');
  }

  // Which cards carry the kind label: 'lead' (the four lead cards; default), 'all' or 'none'.
  function kindLabelsFor(cfg) {
    var v = cfg && cfg.kindLabels;
    return v === 'all' || v === 'none' ? v : 'lead';
  }

  /* ----------------------------------------------------------------------
   * 2. Render-template layer (markup only; all values escaped here)
   * -------------------------------------------------------------------- */
  var templates = {
    // Inline card colours: only the three --ibl-card-* properties, only after
    // cardColours() re-checked every value. `accent` is the contrast-adjusted
    // mark on cards and the raw accent on rail rings (decoration only).
    colourStyle: function (colours, accent) {
      if (!colours) return '';
      return ' style="' + esc(colourStyleValue(colours, accent)) + '"';
    },

    // The card's one image (rev f). A fixed-shape slot: 3:2 above the story,
    // or, from SLOT_TURN[type], 2:3 beside it. A card whose slot turns renders
    // one <picture>: the <source media> carries the portrait image and the
    // <img> the landscape one, so the browser fetches only the image for the
    // current width. data-ibl-landscape / data-ibl-portrait say whether each
    // shape shows its banner ("banner") or the first image whole on a white
    // tile ("tile"); ib-launches.css styles the shape in effect. The first
    // card's image is the page's single eager, high-priority image.
    slot: function (launch, ctx) {
      var F = feed();
      var art = ctx.art;
      var land = art.landscape;
      var port = art.portrait;
      var current = (port && ctx.portraitNow) ? port : land;
      var loading = ctx.eager ? ' loading="eager" fetchpriority="high"' : ' loading="lazy" decoding="async"';
      var img = '<img class="ibl-slot__img" data-ibl-slot-img alt="' + esc(current.alt) + '"' +
        ' src="' + esc(F.imageUrl(land.url, CARD_SRC_WIDTH)) + '"' +
        ' srcset="' + esc(F.imageSrcset(land.url, IMAGE_WIDTHS)) + '"' +
        ' sizes="' + esc(SIZES.landscape[ctx.type]) + '"' + loading + '>';
      var media = img;
      if (port) {
        media = '<picture><source media="(min-width: ' + art.turn + 'px)"' +
          ' srcset="' + esc(F.imageSrcset(port.url, IMAGE_WIDTHS)) + '"' +
          ' sizes="' + esc(SIZES.portrait[ctx.type]) + '">' + img + '</picture>';
      }
      return '<div class="ibl-slot" data-ibl-slot' +
        (port ? ' data-ibl-turn="' + art.turn + '"' : '') +
        ' data-ibl-landscape="' + (land.tile ? 'tile' : 'banner') + '"' +
        (port ? ' data-ibl-portrait="' + (port.tile ? 'tile' : 'banner') + '"' : '') + '>' +
        media + '</div>';
    },

    // Brand, then the kind label as a pill. The kind label shows on the cards
    // the section setting picks (default: the four lead cards).
    eyebrow: function (launch, ctx) {
      var kindLabel = ctx.showKind ? (STRINGS.kind[launch.kind] || '') : '';
      if (!launch.brand && !kindLabel) return '';
      return '<div class="ibl-card__eyebrow">' +
        (launch.brand ? '<span class="ibl-card__brand">' + esc(launch.brand) + '</span>' : '') +
        (kindLabel ? '<span class="ibl-card__kind">' + esc(kindLabel) + '</span>' : '') +
        '</div>';
    },

    title: function (launch, ctx) {
      return '<h2 class="ibl-card__title" id="' + esc(ctx.titleId) + '" tabindex="-1">' + esc(launch.title) + '</h2>';
    },

    // Rev d: the tagline is plain text directly under the title, never truncated.
    tagline: function (launch) {
      return launch.tagline ? '<p class="ibl-card__tagline">' + esc(launch.tagline) + '</p>' : '';
    },

    summary: function (launch, full) {
      var blocks = full ? launch.summary : summaryExcerpt(launch);
      return '<div class="ibl-card__summary' + (full ? '' : ' ibl-card__summary--excerpt') + '">' +
        feed().blocksToHtml(blocks) + '</div>';
    },

    // Numbered highlights. The <ol> carries the order; the "01" marks are
    // decoration in the contrast-adjusted card accent.
    highlights: function (launch) {
      var list = launch.highlights;
      if (!list.length) return '';
      var F = feed();
      var items = '';
      for (var i = 0; i < list.length; i++) {
        items += '<li><span class="ibl-hl__no" aria-hidden="true">' + pad2(i + 1) + '</span>' +
          '<div class="ibl-hl__text">' + F.blocksToHtml([{ type: 'paragraph', text: list[i] }]) + '</div></li>';
      }
      return '<ol class="ibl-card__highlights" data-n="' + list.length + '">' + items + '</ol>';
    },

    // One CTA per card: a real button that opens the launch modal. Its label
    // is fixed by kind; its accessible name includes the title.
    cta: function (launch, filled) {
      var label = STRINGS.cta[launch.kind] || STRINGS.cta.product;
      return '<button type="button" class="ibl-cta ibl-cta--' + (filled ? 'filled' : 'outline') + '" data-ibl-open="' + esc(launch.id) + '">' +
        '<span class="ibl-cta__label">' + esc(label) + '<span class="ibl-sr-only">: ' + esc(launch.title) + '</span></span>' +
        '<span class="ibl-cta__arrow" aria-hidden="true">→</span></button>';
    },

    text: function (launch, ctx) {
      var type = ctx.type;
      if (type === 'feature') {
        return '<div class="ibl-card__text">' +
          templates.eyebrow(launch, ctx) +
          '<div class="ibl-card__head">' + templates.title(launch, ctx) + templates.tagline(launch) + '</div>' +
          '<div class="ibl-card__actions">' + templates.cta(launch, true) + '</div>' +
          templates.summary(launch, true) +
          templates.highlights(launch) +
          '</div>';
      }
      // Lead and stack cards nest the story in the order it can sit beside a
      // portrait: the head (brand, title, tagline), then the lede (head +
      // summary), then the aside (lede + highlights). A lead sets the whole
      // aside beside its portrait; a stack card sets the widest block that
      // fits and runs the rest below (ib-launches.css). Where the slot sits
      // above, the wrappers are layout-neutral. Leads wear the filled button.
      return '<div class="ibl-card__text">' +
        '<div class="ibl-card__aside"><div class="ibl-card__lede">' +
        '<div class="ibl-card__head">' +
        templates.eyebrow(launch, ctx) +
        templates.title(launch, ctx) +
        templates.tagline(launch) +
        '</div>' +
        templates.summary(launch, false) +
        '</div>' +
        templates.highlights(launch) +
        '</div>' +
        templates.cta(launch, type === 'lead') +
        '</div>';
    },

    card: function (launch, index, ctx) {
      var type = ctx.type;
      var colours = ctx.colours;
      var slot = templates.slot(launch, ctx);
      var body = templates.text(launch, ctx);
      // DOM order: story first on a feature (v3); the slot first on lead and
      // stack cards. CSS puts the slot on top wherever it sits above.
      var inner = type === 'feature' ? body + slot : slot + body;
      return '<article class="ibl-card ibl-card--' + type + (colours ? '' : ' ibl-card--house') + '"' +
        (colours && colours.derived ? ' data-ibl-tone="derived"' : '') +
        ' data-ibl-card="' + esc(launch.id) + '" data-ibl-type="' + type + '"' +
        ' aria-labelledby="' + esc(ctx.titleId) + '"' + templates.colourStyle(colours, colours ? colours.mark : '') + '>' +
        inner + '</article>';
    },

    item: function (launch, index, ctx) {
      var slot = ctx.slot;
      return '<div class="ibl-grid__item" role="listitem" id="launch-' + esc(launch.id) + '" data-ibl-item="' + esc(launch.id) + '"' +
        ' data-ibl-pos="' + slot.pos + '" data-ibl-l3="' + slot.l3 + '" data-ibl-t2="' + slot.t2 + '" data-ibl-l2="' + slot.l2 + '">' +
        templates.card(launch, index, ctx) + '</div>';
    },

    meta: function (launchCount, brandCount) {
      return '<span class="ibl-meta__count">' + esc(STRINGS.count(launchCount)) + '</span>' +
        (brandCount ? '<span class="ibl-meta__brands">' + esc(STRINGS.brands(brandCount)) + '</span>' : '');
    },

    railButton: function (launch, current, colours) {
      return '<button type="button" class="ibl-rail__btn" data-ibl-jump="' + esc(launch.id) + '"' +
        (current ? ' aria-current="true"' : '') + templates.colourStyle(colours, colours ? colours.accent : '') + '>' +
        '<span class="ibl-rail__thumb"><img src="' + esc(feed().imageUrl(launch.images[0].url, THUMB_WIDTH)) + '"' +
        ' alt="" loading="lazy" decoding="async"></span>' +
        '<span class="ibl-rail__label">' + railLabelHtml(launch.title) + '</span></button>';
    },

    rail: function (launches, colourOf) {
      var buttons = '';
      for (var i = 0; i < launches.length; i++) buttons += templates.railButton(launches[i], i === 0, colourOf(launches[i]));
      return '<div class="ibl-bar__inner" data-ibl-bar-inner><div class="ibl-bar__pad"><div class="ibl-bar__rule">' +
        '<p class="ibl-bar__label" aria-hidden="true">' + esc(STRINGS.railLabel) + '</p>' +
        '<div class="ibl-rail-wrap">' +
        '<nav class="ibl-rail" data-ibl-rail aria-label="' + esc(STRINGS.railLabel) + '">' + buttons + '</nav>' +
        '<button type="button" class="ibl-rail__scroll ibl-rail__scroll--prev" data-ibl-railnav="prev" tabindex="-1" aria-label="' + esc(STRINGS.railPrev) + '" hidden>' +
        '<span class="ibl-rail__scroll-ring">' + ICONS.railPrev + '</span></button>' +
        '<button type="button" class="ibl-rail__scroll ibl-rail__scroll--next" data-ibl-railnav="next" tabindex="-1" aria-label="' + esc(STRINGS.railNext) + '" hidden>' +
        '<span class="ibl-rail__scroll-ring">' + ICONS.railNext + '</span></button>' +
        '</div></div></div></div>';
    },

    // Ordered launch gallery inside the modal: every feed image, in order, with
    // alt. Shopify product media never replaces it (host.wireThumbs is a no-op),
    // and the banner never joins it.
    modalGallery: function (launch) {
      var F = feed();
      var images = launch.images;
      var lead = images[0];
      var thumbs = '';
      if (images.length > 1) {
        for (var i = 0; i < images.length; i++) {
          thumbs += '<button type="button" class="ibl-gallery__thumb" data-ibl-thumb="' + i + '"' +
            ' aria-label="' + esc(STRINGS.showImage(i + 1, images.length)) + '"' +
            ' aria-pressed="' + (i === 0 ? 'true' : 'false') + '">' +
            '<img src="' + esc(F.imageUrl(images[i].url, THUMB_WIDTH)) + '" alt="" loading="lazy" decoding="async">' +
            '</button>';
        }
        thumbs = '<div class="ibl-gallery__thumbs" role="group" aria-label="' + esc(STRINGS.galleryLabel) + '">' + thumbs + '</div>';
      }
      return '<div class="ibl-gallery" data-ibl-gallery data-ibl-index="0">' +
        '<div class="ibl-gallery__main">' +
        '<img class="ibl-gallery__img" data-ibl-gallery-main' +
        ' src="' + esc(F.imageUrl(lead.url, MODAL_SRC_WIDTH)) + '"' +
        ' srcset="' + esc(F.imageSrcset(lead.url, IMAGE_WIDTHS)) + '"' +
        ' sizes="' + esc(MODAL_SIZES) + '"' +
        ' alt="' + esc(imageAlt(lead, launch)) + '">' +
        '</div>' + thumbs + '</div>';
    },

    modalHead: function (launch) {
      var kindLabel = STRINGS.kind[launch.kind] || '';
      return '<div class="rs-brandline ibl-modal__brandline"><span class="bar"></span>' +
        (launch.brand ? '<span class="name">' + esc(launch.brand) + '</span>' : '') +
        (kindLabel ? '<span class="ibl-modal__kind">' + esc(kindLabel) + '</span>' : '') +
        '</div>' +
        '<h2 id="rsTitle" class="rs-title ibl-modal__title">' + esc(launch.title) + '</h2>' +
        (launch.tagline ? '<p class="ibl-modal__tagline">' + esc(launch.tagline) + '</p>' : '');
    },

    modalSummary: function (launch) {
      return '<div class="rs-desc ibl-modal__summary">' + feed().blocksToHtml(launch.summary) + '</div>';
    },

    modalHighlights: function (launch) {
      if (!launch.highlights.length) return '';
      return '<div class="rs-sec ibl-modal__sec">' +
        '<div class="rs-sec-h"><span class="bar"></span>' + esc(STRINGS.highlights) + '</div>' +
        '<div class="rs-desc ibl-modal__highlights">' +
        feed().blocksToHtml([{ type: 'list', ordered: false, items: launch.highlights }]) +
        '</div></div>';
    },

    modalDescription: function (launch) {
      if (!launch.description) return '';
      return '<div class="rs-sec ibl-modal__sec">' +
        '<div class="rs-sec-h"><span class="bar"></span>' + esc(STRINGS.details) + '</div>' +
        '<div class="rs-desc ibl-modal__description">' + feed().blocksToHtml(launch.description) + '</div></div>';
    },

    // Product launch modal. Keeps the engine hooks: .rs-overlay, .rs-modal
    // (dialog), .rs-close, #rsTitle, [data-sp-receipt], [data-sp-buybody],
    // [data-sp-foot] and [data-sp-close].
    productModal: function (promo, host) {
      var launch = promo.launch;
      return '<div class="rs-overlay ibl-overlay">' +
        '<div class="rs-modal rs-modal--product ibl-modal ibl-modal--product" role="dialog" aria-modal="true" aria-labelledby="rsTitle">' +
        '<button class="rs-close" type="button" aria-label="' + esc(STRINGS.close) + '">' + ICONS.close + '</button>' +
        '<div class="ibl-modal__media">' + templates.modalGallery(launch) + '</div>' +
        '<div class="rs-details ibl-modal__details">' +
        '<div class="rs-scroll ibl-modal__scroll">' +
        templates.modalHead(launch) +
        templates.modalSummary(launch) +
        '<div class="ibl-modal__buy">' +
        '<div data-sp-receipt></div>' +
        '<div data-sp-buybody>' + host.gridStatusHtml(STRINGS.loadingBuy, '') + '</div>' +
        '</div>' +
        templates.modalHighlights(launch) +
        templates.modalDescription(launch) +
        host.footCloseHtml() +
        '</div>' +
        '<div class="rs-foot" data-sp-foot></div>' +
        '</div></div></div>';
    },

    // Collection launch modal. Keeps the engine hooks: #sp-modal-grid,
    // [data-sp-grid-count] and the hidden [data-sp-collfilter] pill.
    collectionModal: function (promo, host) {
      var launch = promo.launch;
      return '<div class="rs-overlay ibl-overlay">' +
        '<div class="rs-modal rs-modal--collection ibl-modal ibl-modal--collection" role="dialog" aria-modal="true" aria-labelledby="rsTitle">' +
        '<button class="rs-close" type="button" aria-label="' + esc(STRINGS.close) + '">' + ICONS.close + '</button>' +
        '<div class="rs-cscroll">' +
        '<div class="rs-chead ibl-modal__head">' +
        '<div class="ibl-modal__media">' + templates.modalGallery(launch) + '</div>' +
        '<div class="rs-cintro ibl-modal__intro">' +
        templates.modalHead(launch) +
        templates.modalSummary(launch) +
        templates.modalHighlights(launch) +
        '</div></div>' +
        '<div class="rs-cbody"><div class="rs-sec">' +
        '<div class="rs-sec-h"><span class="bar"></span>' + esc(STRINGS.inCollection) +
        '<span class="count" data-sp-grid-count></span>' +
        '<button type="button" class="filt-pill rs-collfilter" data-sp-collfilter hidden aria-haspopup="dialog">' + ICONS.filter +
        '<span class="filt-pill-label">' + esc(STRINGS.filter) + '</span>' +
        '<span class="filt-pill-count" data-sp-collfilter-count hidden></span></button></div>' +
        '<div id="sp-modal-grid" class="sp-modal-grid" data-sp-kind="collection">' +
        host.gridStatusHtml(STRINGS.loadingProducts, '') + '</div>' +
        '</div>' +
        // Details follow the shoppable grid so the products sit near the fold.
        templates.modalDescription(launch) +
        host.footCloseHtml() +
        '</div></div></div></div>';
    },

    errorState: function () {
      return '<p class="ibl-status__text">' + esc(STRINGS.error) + '</p>' +
        '<button type="button" class="ibl-status__retry" data-ibl-retry>' + esc(STRINGS.retry) + '</button>';
    }
  };

  /* ----------------------------------------------------------------------
   * 3a. Card-level access: REGION ONLY (contract 6.5, decision O2)
   * -------------------------------------------------------------------- */
  function cardVisible(launch, customer) {
    var policy = window.IBAccessPolicy;
    if (!policy || typeof policy.evaluatePromotionAccess !== 'function') {
      return launch.restrictedRegions.length === 0;            // fail closed on regions only
    }
    var r = policy.evaluatePromotionAccess(
      { restrictedRegions: launch.restrictedRegions, matrixOverridable: launch.matrixOverridable },
      customer, { pricingRestrictionsEnabled: false });
    return !(r && r.restrictionType === 'region_restricted');
  }

  /* ----------------------------------------------------------------------
   * 3b. Modal seam (contract 6.6): reuse ShoppablePromotions.Engine.prototype
   *     through an Object.create host. Zero edits to any promotions file.
   * -------------------------------------------------------------------- */
  function modalAvailable() {
    var SP = window.ShoppablePromotions;
    var PF = window.IBPromoFeed;
    return !!(SP && SP.Engine && SP.Engine.prototype &&
      typeof SP.Engine.prototype.openModal === 'function' &&
      typeof SP.Engine.prototype.receiptHtml === 'function' &&
      PF && typeof PF.resolveSource === 'function');
  }

  function createModalHost(scopeEl, cfg) {          // once per section
    var SP = window.ShoppablePromotions;
    var host = Object.create(SP.Engine.prototype);
    host.root = scopeEl;                             // .shoppable-promotions.ibl-modal-scope[data-sp-booted]
    host.modalRoot = scopeEl.querySelector('[data-ibl-modal-mount]');
    host.cfg = { pricingRestrictionsEnabled: true,   // modal fails CLOSED if IBAccessPolicy is missing
                 customer: cfg.customer || null,
                 endpoints: { collection: 'shoppable-promo-collection' } };
    host.customerState = cfg.customer || null;
    host.lastFocus = null;
    host.challengeBound = false;
    host.productModalHtml = function (promo) { return templates.productModal(promo, this); };
    host.collectionModalHtml = function (promo) { return templates.collectionModal(promo, this); };
    host.modalMetaHtml = function () { return ''; };  // no static "In stock"/date meta
    host.wireThumbs = function () {};                 // R12: Shopify media never replaces the feed gallery
    host.receiptHtml = function (promo, variant, starting) {
      // Live price (and a struck-through compare-at when Shopify has one) via the engine's own formatter.
      // Strip ALL offer chrome: the percent-off chip, the savings aside and the offer foot.
      var html = SP.Engine.prototype.receiptHtml.call(this, promo, variant, starting);
      return html
        .replace(/<span class="rs-pct">[^<]*<\/span>/g, '')
        .replace(/<div class="rs-receipt-aside"><div class="lbl">You save<\/div><div class="rs-save">[^<]*<\/div><\/div>/g, '')
        .replace(/<div class="rs-receipt-foot">[\s\S]*?<\/span><\/div>/g, '');
    };
    host.bindChallenge();
    return host;
  }

  function toModalPromo(launch) {                     // the only promo-shaped object launches create
    return {
      id: 'launch:' + launch.id,                      // cart line property _promo_id
      title: launch.title, subtitle: null, brand: launch.brand || '',
      image: launch.images[0].url, description: null,
      dates: { start: null, end: null, whileSuppliesLast: false, display: '' },
      status: { inStock: true, limitedTime: false },
      tags: [], included: [], terms: null, badge: null, discountLabel: null,
      restrictedRegions: launch.restrictedRegions, matrixOverridable: launch.matrixOverridable,
      launch: launch                                  // launch markup methods read ONLY this
    };
  }

  function navigateTo(href) {
    if (href && window.location) window.location.href = href;
  }

  /* ----------------------------------------------------------------------
   * 3c. Image sample (rev e, D-e4; rev f): one small CORS copy per launch
   *     The sampled image (banner, else portrait banner, else first image)
   *     is fetched once at SAMPLE_WIDTH from the Shopify CDN (CORS-enabled)
   *     and drawn to a 32 x 32 canvas; its pixels give the card colours when
   *     the launch has no Colours line. Any failure (load error, CORS taint,
   *     no canvas) gives no palette, so the card keeps the house palette.
   * -------------------------------------------------------------------- */
  var samples = Object.create(null);

  function readPixels(img) {
    var canvas = document.createElement('canvas');
    if (!canvas || typeof canvas.getContext !== 'function') return null;
    canvas.width = SAMPLE_GRID;
    canvas.height = SAMPLE_GRID;
    var g = canvas.getContext('2d');
    if (!g) return null;
    g.drawImage(img, 0, 0, SAMPLE_GRID, SAMPLE_GRID);
    return g.getImageData(0, 0, SAMPLE_GRID, SAMPLE_GRID).data;   // throws when tainted
  }

  function sampleImage(url, done) {
    var p = samples[url];
    if (p) {
      if (p.done) done(p); else p.waiting.push(done);
      return;
    }
    p = samples[url] = { done: false, palette: null, waiting: [done] };
    function finish(data) {
      if (data) p.palette = paletteFromPixels(data);
      p.done = true;
      var waiting = p.waiting;
      p.waiting = [];
      for (var i = 0; i < waiting.length; i++) waiting[i](p);
    }
    if (typeof window.Image !== 'function') { finish(null); return; }
    var img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      var data = null;
      try { data = readPixels(img); } catch (e) { data = null; }
      finish(data);
    };
    img.onerror = function () { finish(null); };
    img.src = feed().imageUrl(url, SAMPLE_WIDTH);
  }

  /* ----------------------------------------------------------------------
   * 4. Section instance
   * -------------------------------------------------------------------- */
  var uidCounter = 0;

  function readConfig(root) {
    var node = root.querySelector('[data-ibl-config]');
    if (!node) return null;
    try {
      var cfg = JSON.parse(node.textContent);
      return (cfg && typeof cfg === 'object') ? cfg : null;
    } catch (e) {
      return null;
    }
  }

  function currentBand() {
    if (typeof window.matchMedia !== 'function') return 'wide';
    if (window.matchMedia(BANDS.phone).matches) return 'phone';
    if (window.matchMedia(BANDS.tablet).matches) return 'tablet';
    if (window.matchMedia(BANDS.wide).matches) return 'wide';
    return 'desktop';
  }

  function Section(root) {
    this.root = root;
    this.grid = root.querySelector('[data-ibl-grid]');
    this.status = root.querySelector('[data-ibl-status]');
    this.scope = root.querySelector('[data-ibl-modal-scope]');
    this.mount = this.scope ? this.scope.querySelector('[data-ibl-modal-mount]') : null;
    this.meta = root.querySelector('[data-ibl-meta]');
    this.bar = root.querySelector('[data-ibl-bar]');
    this.sentinel = root.querySelector('[data-ibl-sentinel]');
    this.cfg = null;
    this.launches = [];
    this.byId = Object.create(null);
    this.host = null;
    this.uid = 'ibl' + (++uidCounter);
    this.statusBound = false;
    this.railBound = false;
    this.retrying = false;
    this.band = currentBand();
    this.plan = [];
    this.types = [];
    this.offset = 0;
    this.stuck = false;
    this.stuckH = 0;
    this.active = null;
    this.pin = null;
    this.inView = Object.create(null);
    this.observers = {};
    this.watching = false;
    this.kindLabels = 'lead';
    this.tones = Object.create(null);     // id -> re-checked colours derived from the sampled image
    this.destroyed = false;               // set by destroy(); late feed work then stops
    // Rev j: the server-rendered loading skeleton, kept so a retry can show it again.
    this.skeletons = {
      grid: this.grid ? this.grid.innerHTML : '',
      bar: this.bar ? this.bar.innerHTML : '',
      meta: this.meta ? this.meta.innerHTML : ''
    };
  }

  Section.prototype.init = function () {
    if (!this.grid || !this.status) return;
    this.bindGrid();
    this.cfg = readConfig(this.root);
    this.kindLabels = kindLabelsFor(this.cfg);
    if (!feed() || !this.cfg || !this.cfg.feedUrl || typeof fetch !== 'function') {
      this.showError(false);
      return;
    }
    this.load();
  };

  Section.prototype.setBusy = function (busy) {
    this.grid.setAttribute('aria-busy', busy ? 'true' : 'false');
  };

  Section.prototype.setStatus = function (state, text) {
    this.status.className = 'ibl-status ibl-status--' + state;
    this.status.setAttribute('data-ibl-state', state);
    this.status.textContent = text;
  };

  // Rev j: a retry after an error puts the loading skeleton back.
  Section.prototype.showSkeleton = function () {
    var s = this.skeletons;
    if (!this.grid.firstElementChild && s.grid) this.grid.innerHTML = s.grid;
    if (this.bar && !this.bar.firstElementChild && s.bar) {
      this.bar.innerHTML = s.bar;
      this.bar.removeAttribute('hidden');
    }
    if (this.meta && !this.meta.firstElementChild && s.meta) this.meta.innerHTML = s.meta;
  };

  // Rev j: the slots in `scope` whose photos are still loading shimmer until they land.
  Section.prototype.watchImages = function (scope) {
    if (reducedMotion()) return;
    var imgs = scope.querySelectorAll('[data-ibl-slot-img]');
    for (var i = 0; i < imgs.length; i++) watchImage(imgs[i]);
  };

  Section.prototype.load = function () {
    var self = this;
    this.showSkeleton();
    this.setBusy(true);
    this.setStatus('loading', STRINGS.loading);
    fetch(this.cfg.feedUrl, { credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('Launch feed HTTP ' + res.status);
        return res.json();
      })
      .then(function (json) { if (!self.destroyed) self.ingest(json); })
      .catch(function (err) {
        if (self.destroyed) return;
        if (typeof console !== 'undefined' && console && console.error) {
          console.error('[IBLaunches] launch feed failed to load', err);
        }
        self.showError(true);
      });
  };

  Section.prototype.ingest = function (json) {
    var result = feed().validateFeed(json);
    if (!result.ok) { this.showError(true); return; }
    if (result.rejected.length && typeof console !== 'undefined' && console && console.warn) {
      console.warn('[IBLaunches] skipped ' + result.rejected.length + ' invalid launch entr' +
        (result.rejected.length === 1 ? 'y' : 'ies'), result.rejected);
    }
    var customer = this.cfg.customer || null;
    var visible = [];
    for (var i = 0; i < result.launches.length; i++) {
      if (cardVisible(result.launches[i], customer)) visible.push(result.launches[i]);
    }
    var self = this;
    this.prepareTones(visible, function () { if (!self.destroyed) self.render(visible); });
  };

  // Colours for a card: an authored Colours line that passes the re-check
  // always wins; then colours derived from the slot image; else null (house).
  Section.prototype.coloursFor = function (launch) {
    return cardColours(launch) || this.tones[launch.id] || null;
  };

  // Sample each launch's image once (banner, else portrait banner, else the
  // first image) when it has no valid Colours line. The first render waits up
  // to TONE_WAIT_MS for the first cards' samples so the cards on screen at
  // load open in their own colours; later samples are applied as they land.
  Section.prototype.prepareTones = function (launches, done) {
    var self = this;
    var pending = 0;
    var looping = true;
    var finished = false;
    var timer = null;
    function go() {
      if (finished) return;
      finished = true;
      if (timer) clearTimeout(timer);
      done();
    }
    for (var i = 0; i < launches.length; i++) {
      var launch = launches[i];
      if (cardColours(launch) || this.tones[launch.id]) continue;
      var critical = i < TONE_FIRST_CARDS;
      if (critical) pending += 1;
      sampleImage(sampledImage(launch).url, (function (l, counted) {
        return function (sample) {
          if (sample.palette) self.setTone(l, sample.palette);
          if (!counted) return;
          pending -= 1;
          if (!looping && pending === 0) go();
        };
      })(launch, critical));
    }
    looping = false;
    if (pending === 0) go(); else timer = setTimeout(go, TONE_WAIT_MS);
  };

  // A derived palette goes through the same re-check as an authored one.
  Section.prototype.setTone = function (launch, palette) {
    var checked = cardColours({ colours: palette });
    if (!checked) return;
    checked.derived = true;
    this.tones[launch.id] = checked;
    if (!cardColours(launch)) this.applyTone(launch.id, checked);
  };

  // Swap a rendered card (and its rail ring) from the house palette to its
  // derived colours. Only the style attribute changes, so nothing moves; the
  // colour change animates unless reduced motion is on (ib-launches.css).
  Section.prototype.applyTone = function (id, colours) {
    var el = this.grid.querySelector('[data-ibl-card="' + id + '"]');
    if (el) {
      el.setAttribute('style', colourStyleValue(colours, colours.mark));
      el.classList.remove('ibl-card--house');
      el.setAttribute('data-ibl-tone', 'derived');
    }
    var ring = this.bar ? this.bar.querySelector('[data-ibl-jump="' + id + '"]') : null;
    if (ring) ring.setAttribute('style', colourStyleValue(colours, colours.accent));
  };

  // Per-card render context: type for the current width band, colours, what
  // the slot shows (and which shape is in effect now) and the title id. The
  // kind pill follows the plan position, so a lead keeps it as a feature.
  Section.prototype.cardContext = function (launch, index) {
    var slot = this.plan[index];
    var type = cardType(slot, this.band);
    var art = slotArt(launch, type);
    return {
      type: type,
      slot: slot,
      colours: this.coloursFor(launch),
      art: art,
      portraitNow: portraitNow(art.turn),
      eager: index === 0,
      showKind: this.kindLabels === 'all' || (this.kindLabels === 'lead' && slot.pos === 'lead'),
      titleId: this.uid + '-title-' + index
    };
  };

  Section.prototype.render = function (launches) {
    this.launches = launches;
    this.byId = Object.create(null);
    if (!launches.length) {
      this.grid.innerHTML = '';
      this.setBusy(false);
      this.setStatus('empty', this.cfg.emptyText || STRINGS.empty);
      this.renderChrome();
      this.retrying = false;
      return;
    }
    this.plan = layoutPlan(launches.length);
    this.types = [];
    var html = '';
    for (var i = 0; i < launches.length; i++) {
      var launch = launches[i];
      var ctx = this.cardContext(launch, i);
      this.byId[launch.id] = launch;
      this.types.push(ctx.type);
      html += templates.item(launch, i, ctx);
    }
    this.grid.innerHTML = html;
    this.watchImages(this.grid);
    this.setBusy(false);
    this.setStatus('ready', STRINGS.loaded(launches.length));
    this.renderChrome();
    if (this.retrying) this.focusFirstCard();
    this.enhance();
  };

  // The header count and the rail. Both are hidden when there is nothing to show.
  Section.prototype.renderChrome = function () {
    var launches = this.launches;
    if (this.meta) {
      var brands = Object.create(null);
      var brandCount = 0;
      for (var i = 0; i < launches.length; i++) {
        if (!launches[i].brand) continue;
        var key = String(launches[i].brand).toLowerCase().replace(/[‘’]/g, '\'');
        if (!brands[key]) { brands[key] = true; brandCount += 1; }
      }
      this.meta.innerHTML = launches.length ? templates.meta(launches.length, brandCount) : '';
    }
    if (this.bar) {
      if (!launches.length) {
        this.bar.innerHTML = '';
        this.bar.setAttribute('hidden', '');
        return;
      }
      this.bar.removeAttribute('hidden');
      var self = this;
      this.bar.innerHTML = templates.rail(launches, function (launch) { return self.coloursFor(launch); });
      this.active = launches[0].id;
      this.bindRail();
    }
  };

  // After a successful retry the focused status region becomes visually hidden,
  // so hand focus to the first card's CTA and drop the status tabindex.
  Section.prototype.focusFirstCard = function () {
    this.retrying = false;
    this.status.removeAttribute('tabindex');
    var first = this.grid.querySelector('[data-ibl-open]');
    if (first && first.focus) first.focus();
  };

  Section.prototype.showError = function (canRetry) {
    this.grid.innerHTML = '';
    this.setBusy(false);
    if (this.bar) { this.bar.innerHTML = ''; this.bar.setAttribute('hidden', ''); }
    if (this.meta) this.meta.innerHTML = '';
    this.status.className = 'ibl-status ibl-status--error';
    this.status.setAttribute('data-ibl-state', 'error');
    if (!canRetry || !feed()) {
      this.status.textContent = STRINGS.error;
      return;
    }
    this.status.innerHTML = templates.errorState();
    if (this.statusBound) return;
    this.statusBound = true;
    var self = this;
    this.status.addEventListener('click', function (e) {
      if (!closest(e.target, '[data-ibl-retry]')) return;
      // Keep focus inside the live region while the retry button is replaced.
      self.status.setAttribute('tabindex', '-1');
      if (self.status.focus) self.status.focus();
      self.retrying = true;
      self.load();
    });
  };

  // One delegated click handler on the grid. Enter and Space on the CTA
  // <button> dispatch `click` natively, so it is also the keyboard path. A
  // click anywhere on a card opens its modal; any other in-card control
  // would carry data-ibl-stop (rev f: cards have none).
  Section.prototype.bindGrid = function () {
    var self = this;
    this.grid.addEventListener('click', function (e) {
      var target = e.target;
      if (closest(target, '[data-ibl-stop]')) return;
      var card = closest(target, '[data-ibl-card]');
      if (!card) return;
      // Dragging to select card text ends in a click; let shoppers copy text.
      if (!closest(target, '[data-ibl-open]') && hasTextSelection()) return;
      var trigger = card.querySelector('[data-ibl-open]') || card;
      self.open(card.getAttribute('data-ibl-card'), trigger);
    });
  };

  /* ---- Browser-only enhancements (observers; skipped where unsupported) ---- */

  Section.prototype.enhance = function () {
    this.watchBands();
    this.prepReveal();
    this.measureOffset();
    this.observeStuck();
    this.observeActive();
    this.observeHeader();
    this.observeRail();
    this.jumpToHash();
  };

  // The width bands, plus each slot turn width (the picture swaps the image
  // there by itself; the page only keeps the slot's alt in step).
  Section.prototype.watchBands = function () {
    if (this.watching || typeof window.matchMedia !== 'function') return;
    this.watching = true;
    var self = this;
    var onChange = function () { self.bandChanged(); };
    var queries = [];
    var key;
    for (key in BANDS) {
      if (Object.prototype.hasOwnProperty.call(BANDS, key)) queries.push(BANDS[key]);
    }
    for (key in SLOT_TURN) {
      if (!Object.prototype.hasOwnProperty.call(SLOT_TURN, key) || !SLOT_TURN[key]) continue;
      var q = '(min-width: ' + SLOT_TURN[key] + 'px)';
      if (queries.indexOf(q) === -1) queries.push(q);
    }
    var lists = [];
    for (var i = 0; i < queries.length; i++) {
      var mql = window.matchMedia(queries[i]);
      if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onChange);
      else if (typeof mql.addListener === 'function') mql.addListener(onChange);
      lists.push(mql);
    }
    this.unwatchBands = function () {
      lists.forEach(function (list) {
        if (typeof list.removeEventListener === 'function') list.removeEventListener('change', onChange);
        else if (typeof list.removeListener === 'function') list.removeListener(onChange);
      });
      self.watching = false;
    };
  };

  // The width changed: re-render only the cards whose type changed (a lone
  // last card becomes or stops being a feature; its slot follows the type),
  // then keep every slot's alt on the image its <picture> now shows.
  Section.prototype.bandChanged = function () {
    var band = currentBand();
    var changed = band !== this.band;
    this.band = band;
    if (!this.launches.length) return;
    for (var i = 0; i < this.launches.length; i++) {
      var launch = this.launches[i];
      var item = this.grid.querySelector('[data-ibl-item="' + launch.id + '"]');
      if (!item) continue;
      var ctx = this.cardContext(launch, i);
      if (changed && ctx.type !== this.types[i]) {
        this.types[i] = ctx.type;
        item.innerHTML = templates.card(launch, i, ctx);
        this.watchImages(item);
        continue;
      }
      var img = item.querySelector('[data-ibl-slot-img]');
      var shown = (ctx.art.portrait && ctx.portraitNow) ? ctx.art.portrait : ctx.art.landscape;
      if (img && img.getAttribute('alt') !== shown.alt) img.setAttribute('alt', shown.alt);
    }
  };

  // Every card fades and rises into place (v3; rev j, D-j3): the cards on
  // screen at load straight away, the rest as they scroll in, three to a row
  // with a short stagger. Reduced motion: no hiding at all.
  Section.prototype.prepReveal = function () {
    if (typeof window.IntersectionObserver !== 'function' || reducedMotion()) return;
    var self = this;
    if (this.observers.reveal) this.observers.reveal.disconnect();
    this.observers.reveal = new window.IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) self.reveal(entries[i].target);
      }
    }, { threshold: 0, rootMargin: '0px 0px 80px 0px' });
    var items = this.grid.querySelectorAll('[data-ibl-item]');
    for (var k = 0; k < items.length; k++) {
      items[k].setAttribute('data-ibl-reveal', 'wait');
      items[k].setAttribute('data-ibl-stagger', String(k % 3));
      this.observers.reveal.observe(items[k]);
    }
  };

  Section.prototype.reveal = function (item) {
    if (!item || item.getAttribute('data-ibl-reveal') !== 'wait') return;
    item.setAttribute('data-ibl-reveal', 'in');
    if (this.observers.reveal) this.observers.reveal.unobserve(item);
  };

  /* ---- Sticky rail: offset under the theme header, stuck state, tracking ---- */

  // The visible height of the theme's sticky header (.header-section): its
  // wrapper plus any sub-bar that is showing. 0 when the header is not sticky.
  Section.prototype.headerHeight = function () {
    if (typeof window.getComputedStyle !== 'function') return 0;
    var hs = document.querySelector('.header-section');
    if (!hs) return 0;
    var cs = window.getComputedStyle(hs);
    if (cs.position !== 'sticky' && cs.position !== 'fixed') return 0;
    var top = hs.getBoundingClientRect().top;
    var bottom = top;
    var parts = hs.querySelectorAll('#HeaderWrapper, .site-header__element--sub');
    for (var i = 0; i < parts.length; i++) {
      if (!parts[i].offsetHeight || window.getComputedStyle(parts[i]).display === 'none') continue;
      bottom = Math.max(bottom, parts[i].getBoundingClientRect().bottom);
    }
    return Math.max(0, Math.round(bottom - top + (parseFloat(cs.top) || 0)));
  };

  // Returns true when the offset changed.
  Section.prototype.measureOffset = function () {
    var off = this.headerHeight();
    if (off === this.offset) return false;
    this.offset = off;
    setVar(this.root, '--ibl-sticky-offset', off + 'px');
    return true;
  };

  // Re-measure when the theme header changes size (it compresses on scroll).
  Section.prototype.observeHeader = function () {
    if (this.observers.header || typeof window.ResizeObserver !== 'function') return;
    var hs = document.querySelector('.header-section');
    if (!hs) return;
    var self = this;
    this.observers.header = new window.ResizeObserver(function () {
      if (!self.measureOffset()) return;
      self.observeStuck();
      self.observeActive();
    });
    var parts = hs.querySelectorAll('#HeaderWrapper, .site-header__element--sub');
    this.observers.header.observe(hs);
    for (var i = 0; i < parts.length; i++) this.observers.header.observe(parts[i]);
  };

  // The rail is stuck once the zero-height sentinel above it passes under the
  // header. While stuck, the bar keeps its unstuck height (the white part
  // shrinks and the rest turns click-through), so the page never jumps.
  Section.prototype.observeStuck = function () {
    if (!this.sentinel || !this.bar || typeof window.IntersectionObserver !== 'function') return;
    var self = this;
    if (this.observers.stuck) this.observers.stuck.disconnect();
    this.observers.stuck = new window.IntersectionObserver(function (entries) {
      var e = entries[entries.length - 1];
      self.setStuck(!e.isIntersecting && e.boundingClientRect.top < self.offset + 1);
    }, { threshold: 0, rootMargin: (-(this.offset + 1)) + 'px 0px 0px 0px' });
    this.observers.stuck.observe(this.sentinel);
  };

  Section.prototype.setStuck = function (stuck) {
    if (stuck === this.stuck || !this.bar) return;
    var inner = this.bar.querySelector('[data-ibl-bar-inner]');
    if (stuck) {
      this.bar.style.height = this.bar.getBoundingClientRect().height + 'px';
      this.bar.classList.add('is-stuck');
    } else {
      this.bar.classList.remove('is-stuck');
      this.bar.style.height = '';
    }
    this.stuck = stuck;
    if (stuck && inner && inner.offsetHeight && inner.offsetHeight !== this.stuckH) {
      this.stuckH = inner.offsetHeight;
      setVar(this.root, '--ibl-rail-h', this.stuckH + 'px');
      this.observeActive();
    }
    this.updateActive();
    this.updateRailEnds();
  };

  // The current launch is the first card (feed order) whose bottom is still
  // more than 80px below the stuck rail, so a shared row resolves to its
  // left-most card (v3). One observer: a card "intersects" while its bottom is
  // below that line.
  Section.prototype.observeActive = function () {
    if (typeof window.IntersectionObserver !== 'function') return;
    var self = this;
    if (this.observers.active) this.observers.active.disconnect();
    var line = this.offset + (this.stuckH || 114) + 24 + 80;
    this.inView = Object.create(null);
    this.observers.active = new window.IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        self.inView[entries[i].target.getAttribute('data-ibl-item')] = entries[i].isIntersecting;
      }
      self.updateActive();
    }, { threshold: 0, rootMargin: (-line) + 'px 0px 100000px 0px' });
    var items = this.grid.querySelectorAll('[data-ibl-item]');
    for (var k = 0; k < items.length; k++) this.observers.active.observe(items[k]);
  };

  Section.prototype.updateActive = function () {
    if (!this.launches.length) return;
    var auto = null;
    for (var i = 0; i < this.launches.length; i++) {
      if (this.inView[this.launches[i].id]) { auto = this.launches[i].id; break; }
    }
    var active = this.stuck ? (auto || this.active) : this.launches[0].id;
    // A launch picked from the rail stays current until the page moves on.
    var pin = this.pin;
    if (pin) {
      var y = window.pageYOffset || 0;
      if (pin.y === null && now() > pin.until) pin.y = y;
      var moved = pin.y !== null && Math.abs(y - pin.y) > 48;
      if (!moved || this.sameRow(pin.id, auto)) active = pin.id; else this.pin = null;
    }
    this.setActive(active);
  };

  Section.prototype.sameRow = function (a, b) {
    if (!a || !b) return false;
    var ea = this.grid.querySelector('[data-ibl-item="' + a + '"]');
    var eb = this.grid.querySelector('[data-ibl-item="' + b + '"]');
    return !!(ea && eb && Math.abs(ea.getBoundingClientRect().top - eb.getBoundingClientRect().top) < 4);
  };

  Section.prototype.setActive = function (id) {
    if (!id || !this.bar) return;
    var changed = id !== this.active;
    this.active = id;
    var buttons = this.bar.querySelectorAll('[data-ibl-jump]');
    for (var i = 0; i < buttons.length; i++) {
      if (buttons[i].getAttribute('data-ibl-jump') === id) buttons[i].setAttribute('aria-current', 'true');
      else buttons[i].removeAttribute('aria-current');
    }
    if (changed) this.railFollow();
  };

  // Keep the current launch's ring in view inside the rail.
  Section.prototype.railFollow = function () {
    var rail = this.bar && this.bar.querySelector('[data-ibl-rail]');
    if (!rail || typeof rail.scrollTo !== 'function' || !rail.getBoundingClientRect) return;
    var behavior = reducedMotion() ? 'auto' : 'smooth';
    if (!this.stuck && !this.pin && this.launches.length && this.active === this.launches[0].id) {
      if (rail.scrollLeft > 0) rail.scrollTo({ left: 0, behavior: behavior });
      return;
    }
    var btn = rail.querySelector('[aria-current="true"]');
    if (!btn) return;
    var b = btn.getBoundingClientRect();
    var r = rail.getBoundingClientRect();
    if (b.left < r.left + 84 || b.right > r.right - 84) {
      rail.scrollTo({ left: rail.scrollLeft + b.left - r.left - r.width / 2 + b.width / 2, behavior: behavior });
    }
  };

  Section.prototype.bindRail = function () {
    if (this.railBound || !this.bar) return;
    this.railBound = true;
    var self = this;
    this.bar.addEventListener('click', function (e) {
      var nav = closest(e.target, '[data-ibl-railnav]');
      if (nav) { self.railBy(nav.getAttribute('data-ibl-railnav') === 'prev' ? -1 : 1); return; }
      var jump = closest(e.target, '[data-ibl-jump]');
      // detail 0: activated from the keyboard, so focus follows to the card.
      if (jump) self.jumpTo(jump.getAttribute('data-ibl-jump'), e.detail === 0);
    });
  };

  // The rail's own scroll position shows or hides its edge arrows.
  Section.prototype.observeRail = function () {
    var rail = this.bar && this.bar.querySelector('[data-ibl-rail]');
    if (!rail || rail === this.railEl) { this.updateRailEnds(); return; }
    var self = this;
    this.railEl = rail;
    rail.addEventListener('scroll', function () { self.updateRailEnds(); }, { passive: true });
    if (typeof window.ResizeObserver === 'function') {
      if (this.observers.rail) this.observers.rail.disconnect();
      this.observers.rail = new window.ResizeObserver(function () { self.updateRailEnds(); });
      this.observers.rail.observe(rail);
    }
    this.updateRailEnds();
  };

  Section.prototype.updateRailEnds = function () {
    var rail = this.railEl;
    if (!rail || !this.bar) return;
    var atStart = rail.scrollLeft < 4;
    var atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
    var prev = this.bar.querySelector('[data-ibl-railnav="prev"]');
    var next = this.bar.querySelector('[data-ibl-railnav="next"]');
    if (prev) { if (atStart) prev.setAttribute('hidden', ''); else prev.removeAttribute('hidden'); }
    if (next) { if (atEnd) next.setAttribute('hidden', ''); else next.removeAttribute('hidden'); }
  };

  Section.prototype.railBy = function (dir) {
    var rail = this.railEl;
    if (!rail || typeof rail.scrollBy !== 'function') return;
    rail.scrollBy({ left: dir * rail.clientWidth * 0.8, behavior: reducedMotion() ? 'auto' : 'smooth' });
  };

  // Scroll so the launch sits just under the stuck rail, then settle once the
  // smooth scroll lands (the stuck rail's height is only certain then).
  Section.prototype.jumpTo = function (id, moveFocus) {
    var item = this.grid.querySelector('[data-ibl-item="' + id + '"]');
    if (!item || typeof window.scrollTo !== 'function') return;
    this.reveal(item);
    this.pin = { id: id, until: now() + 1500, y: null, settled: false };
    this.setActive(id);
    var inner = this.bar ? this.bar.querySelector('[data-ibl-bar-inner]') : null;
    // Until the rail has been stuck once, estimate its stuck height: no label
    // row, and smaller rings except on phones.
    var barH = this.stuckH || (inner ? inner.offsetHeight - (this.band === 'phone' ? 21 : 43) : 0);
    var top = item.getBoundingClientRect().top + (window.pageYOffset || 0) - this.offset - barH - 16;
    var smooth = !reducedMotion();
    window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: smooth ? 'smooth' : 'auto' });
    if (moveFocus) {
      var heading = item.querySelector('h2');
      if (heading && heading.focus) {
        try { heading.focus({ preventScroll: true }); } catch (e) { heading.focus(); }
      }
    }
    var self = this;
    this.whenScrollSettles(function () { self.settleJump(); });
  };

  Section.prototype.whenScrollSettles = function (done) {
    if (typeof window.requestAnimationFrame !== 'function') { done(); return; }
    var start = now();
    var last = -1;
    var still = 0;
    function tick() {
      var y = window.pageYOffset || 0;
      still = y === last ? still + 1 : 0;
      last = y;
      if (still >= 6 || now() - start > 1600) { done(); return; }
      window.requestAnimationFrame(tick);
    }
    window.requestAnimationFrame(tick);
  };

  Section.prototype.settleJump = function () {
    var pin = this.pin;
    if (!pin || pin.settled) return;
    pin.settled = true;
    var item = this.grid.querySelector('[data-ibl-item="' + pin.id + '"]');
    var inner = this.bar ? this.bar.querySelector('[data-ibl-bar-inner]') : null;
    if (item && inner) {
      var dy = item.getBoundingClientRect().top - (this.offset + inner.offsetHeight + 16);
      if (Math.abs(dy) > 2 && typeof window.scrollBy === 'function') window.scrollBy(0, dy);
    }
    pin.until = 0;
    pin.y = window.pageYOffset || 0;
  };

  // A link to /pages/...#launch-<id> lands on that card once it exists.
  Section.prototype.jumpToHash = function () {
    var hash = window.location && window.location.hash;
    if (!hash || hash.indexOf('#launch-') !== 0) return;
    var id = hash.slice(8);
    if (!this.byId[id]) return;
    var self = this;
    var go = function () { self.jumpTo(id, false); };
    if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(go); else go();
  };

  /* PR #158 review 2 (2026-10-16), theme editor only: Shopify replaces the section
     on every setting change. A launch modal open at that moment went with the old
     DOM but kept the engine's body scroll lock and its document keydown handler,
     so the preview could not scroll. Everything the section holds outside its own
     DOM is released here, and work still in flight (the feed, the colour
     sampling) stops when it lands (PR #158 CodeRabbit, 2026-10-16). */
  Section.prototype.destroy = function () {
    this.destroyed = true;
    if (this.host) {
      if (this.host._modalKeyHandler) this.host.closeModal();
      this.host.unbindChallenge();
      this.host = null;
    }
    var observers = this.observers;
    Object.keys(observers).forEach(function (key) {
      if (observers[key] && typeof observers[key].disconnect === 'function') observers[key].disconnect();
    });
    this.observers = {};
    if (this.unwatchBands) this.unwatchBands();
    this.unwatchBands = null;
  };

  /* ---- Modal ---- */

  Section.prototype.open = function (id, trigger) {
    var launch = this.byId[id];
    if (!launch) return;
    var href = feed().destinationHref(launch);
    if (!modalAvailable() || !this.scope || !this.mount) { navigateTo(href); return; }
    try {
      if (!this.host) this.host = createModalHost(this.scope, this.cfg);
      this.host.openModal({
        promo: toModalPromo(launch),
        resolved: window.IBPromoFeed.resolveSource({ source: {
          type: launch.destination.kind, handle: launch.destination.handle, fallbackLink: launch.destination.url } })
      }, trigger);
    } catch (err) {
      if (typeof console !== 'undefined' && console && console.error) {
        console.error('[IBLaunches] launch modal failed; opening the page instead', err);
      }
      navigateTo(href);
      return;
    }
    this.bindModalGallery(launch);
  };

  Section.prototype.bindModalGallery = function (launch) {
    var gallery = this.mount.querySelector('[data-ibl-gallery]');
    if (!gallery || launch.images.length < 2) return;
    var F = feed();
    gallery.addEventListener('click', function (e) {
      var btn = closest(e.target, '[data-ibl-thumb]');
      if (!btn) return;
      var idx = parseInt(btn.getAttribute('data-ibl-thumb'), 10);
      var image = launch.images[idx];
      var main = gallery.querySelector('[data-ibl-gallery-main]');
      if (!image || !main) return;
      main.setAttribute('srcset', F.imageSrcset(image.url, IMAGE_WIDTHS));
      main.setAttribute('src', F.imageUrl(image.url, MODAL_SRC_WIDTH));
      main.setAttribute('alt', imageAlt(image, launch));
      var thumbs = gallery.querySelectorAll('[data-ibl-thumb]');
      for (var i = 0; i < thumbs.length; i++) {
        thumbs[i].setAttribute('aria-pressed', thumbs[i] === btn ? 'true' : 'false');
      }
      gallery.setAttribute('data-ibl-index', String(idx));
    });
  };

  /* ----------------------------------------------------------------------
   * 5. Bootstrap (defer-safe; mirrors shoppable-promotions.js)
   * -------------------------------------------------------------------- */
  var api = {
    STRINGS: STRINGS,
    SIZES: SIZES,
    templates: templates,
    cardVisible: cardVisible,
    cardColours: cardColours,
    paletteFromPixels: paletteFromPixels,
    SLOT_TURN: SLOT_TURN,
    slotArt: slotArt,
    sampledImage: sampledImage,
    watchImage: watchImage,
    contrastRatio: function (a, b) { return contrastRatio(rgbOf(a), rgbOf(b)); },
    layoutPlan: layoutPlan,
    cardType: cardType,
    summaryExcerpt: summaryExcerpt,
    modalAvailable: modalAvailable,
    createModalHost: createModalHost,
    toModalPromo: toModalPromo,
    Section: Section,
    boot: boot,
    _sections: []
  };

  function boot() {
    var roots = document.querySelectorAll('[data-ibl-root]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (root.getAttribute('data-ibl-booted')) continue;
      root.setAttribute('data-ibl-booted', '1');
      var section = new Section(root);
      api._sections.push(section);
      section.init();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    setTimeout(boot, 0);
  }
  // Theme editor only (Shopify.designMode): release a section Shopify is about to
  // replace, and boot the re-rendered one. On the storefront no document-level
  // listener is added (contract 6.8).
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var target = e && e.target;
      if (!target || (target.querySelector && target.querySelector('[data-ibl-root]'))) boot();
    });
    document.addEventListener('shopify:section:unload', function (e) {
      var target = e && e.target;
      if (!target || typeof target.contains !== 'function') return;
      api._sections = api._sections.filter(function (section) {
        if (!target.contains(section.root)) return true;
        section.destroy();
        return false;
      });
    });
  }

  return api;
})();
